import {Component, HostListener, OnInit} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {MatDialogRef} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {
  BaseDialogComponent,
  REF_MODEL_STORE,
  RefModelStoreService,
  PermissionsService
} from '@q9elements/ui-kit/common';
import {Q9Validators} from '@q9elements/ui-kit/validators';
import {EMPTY, Observable} from 'rxjs';
import {pairwise, startWith, take} from 'rxjs/operators';

import {AVAILABLE_METADATA_TYPES, SEARCH_TYPES} from '../../models/enum/code-search-types.enum';

@Component({
  selector: 'app-run-toolbar-code-search-dialog',
  templateUrl: './run-toolbar-code-search-dialog.component.html',
  styleUrls: ['./run-toolbar-code-search-dialog.component.scss']
})
export class RunToolbarCodeSearchDialogComponent extends BaseDialogComponent implements OnInit {
  @HostListener('document:keydown.enter', ['$event'])
  public supportCodeSearch = 'https://support.elements.cloud/en/articles/8842489-metadata-search';
  public validationForm: FormGroup;
  public searchValueMinLength = 2;
  public searchValueMaxLength = 31;
  public searchTypes = SEARCH_TYPES;
  public matadataTypes = [];
  public readonly GLOBAL_SEARCH_VALUE = 'Global search';
  public isFree = this.permissionService.isFree();

  constructor(
    public dialogRef: MatDialogRef<RunToolbarCodeSearchDialogComponent>,
    private readonly rmeStore: RefModelStoreService,
    private readonly permissionService: PermissionsService,
    private router: Router
  ) {
    super();
  }

  ngOnInit() {
    this.getAvailableMetadataTypes();
    this.initForm();
    this.setupConditionalValidation();
    this.setupMetadataTypeLogic();
  }

  private setupConditionalValidation() {
    this.selectedType.valueChanges.pipe(this.takeUntilComponentDestroy()).subscribe(type => {
      const searchValueControl = this.validationForm.get('searchValue');
      if (type === 'text') {
        searchValueControl.setValidators([
          Q9Validators.required(),
          Q9Validators.minLength(this.searchValueMinLength),
          Q9Validators.maxLength(this.searchValueMaxLength),
          this.noWhitespaceValidator
        ]);
      } else {
        searchValueControl.clearValidators();
      }
      searchValueControl.updateValueAndValidity();
    });
  }

  private setupMetadataTypeLogic() {
    this.metadataType.valueChanges
      .pipe(startWith([]), pairwise(), this.takeUntilComponentDestroy())
      .subscribe(([previous, current]: [string[], string[]]) => {
        if (!current || current.length === 0) {
          return;
        }

        const hasGlobalSearch = current.includes(this.GLOBAL_SEARCH_VALUE);
        const hadGlobalSearch = (previous || []).includes(this.GLOBAL_SEARCH_VALUE);
        const hasMultipleSelections = current.length > 1;

        if (hasGlobalSearch && hasMultipleSelections) {
          const isGlobalSearchNew = !hadGlobalSearch;

          if (isGlobalSearchNew) {
            this.metadataType.setValue([this.GLOBAL_SEARCH_VALUE], {emitEvent: false});
          } else {
            const filtered = current.filter(v => v !== this.GLOBAL_SEARCH_VALUE);
            this.metadataType.setValue(filtered, {emitEvent: false});
          }
        }
      });
  }

  public isOptionDisabled(optionValue: string): boolean {
    const currentValues = this.metadataType?.value || [];
    const hasGlobalSearch = currentValues.includes(this.GLOBAL_SEARCH_VALUE);
    const hasOtherValues = currentValues.length > 0 && !hasGlobalSearch;

    if (optionValue === this.GLOBAL_SEARCH_VALUE) {
      return hasOtherValues;
    }

    return hasGlobalSearch;
  }

  public onEnterKey(event: KeyboardEvent) {
    event.preventDefault();
    this.onSubmit();
  }

  private getAvailableMetadataTypes() {
    this.rmeStore
      .select(REF_MODEL_STORE.REF_MODEL_NODES)
      .pipe(take(1))
      .subscribe((nodes: any[]) => {
        const filteredNodes = (nodes || []).filter(node =>
          AVAILABLE_METADATA_TYPES.includes(node.identity)
        );

        const allItems = [
          {value: 'Global search', name: 'Global search'},
          // Will be implemented in future releases
          // {value: 'Fields', name: 'Fields'},
          // {value: 'Records', name: 'Records'},
          ...filteredNodes.map(node => ({
            value: node._id,
            name: node.name
          }))
        ];

        const globalSearch = allItems[0];
        const restItems = allItems.slice(1).sort((a, b) => a.name.localeCompare(b.name));

        if (this.permissionService.isFree()) {
          this.matadataTypes = [globalSearch];
          return;
        }

        this.matadataTypes = [globalSearch, ...restItems];
      });
  }

  private initForm() {
    this.validationForm = new FormGroup({
      selectedType: new FormControl(null, Q9Validators.required()),
      metadataType: new FormControl(null, Q9Validators.required()),
      searchValue: new FormControl(null)
    });
  }

  get selectedType() {
    return this.validationForm.get('selectedType');
  }

  get metadataType() {
    return this.validationForm.get('metadataType');
  }

  override onSubmit() {
    if (!this.validationForm.valid) {
      this.validationForm.markAllAsTouched();
      this.validationForm.markAsDirty();
      return;
    }

    this.sendSubmitRequest();
  }

  public noWhitespaceValidator(control: FormControl) {
    return (control.value || '').trim().length >= 2 ? null : {whitespace: true};
  }

  protected override sendSubmitRequest(): Observable<unknown> {
    const payload: any = {
      type: this.selectedType.value
    };

    if (this.validationForm.get('searchValue')?.value) {
      payload.search = this.validationForm.get('searchValue').value;
    }

    if (this.metadataType.value && this.metadataType.value.length > 0) {
      const isGlobalSearch = this.metadataType.value.includes(this.GLOBAL_SEARCH_VALUE);
      if (!isGlobalSearch) {
        payload.rmn = this.metadataType.value;
      }
    }

    const url = this.router.serializeUrl(
      this.router.createUrlTree([`code-search`, this.rmeStore.refModel._id], {
        relativeTo: null,
        queryParams: payload
      })
    );

    window.open(url, '_blank');
    this.dialogRef.close();

    return EMPTY;
  }
}
